/*
 * Separator (horizontal star bridge)
 *
 * Two star nodes connected by a peach path. Visible length tracks scroll
 * as the separator crosses the viewport — or, when
 * data-separator-scroll-source is set, tracks that element's horizontal
 * scroll progress (sticky image cards on mobile).
 */
(function () {
  'use strict';

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var TRACK_Y = 20;
  var INSET = 20;
  var PULSE_DURATION = 1000;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 989px)');

  function Separator(root) {
    this.root = root;
    this.svg = root.querySelector('.separator__svg');
    this.path = root.querySelector('[data-separator-path]');
    this.lineGradient = root.querySelector('[data-separator-gradient]');
    this.nodesLayer = root.querySelector('[data-separator-nodes]');
    this.template = root.querySelector('[data-separator-node-template]');
    this.progressHost = root.closest('[data-sticky-image-progress]');
    this.nodes = [];
    this.litIndex = -1;
    this.ticking = false;
    this.startX = INSET;
    this.endX = INSET;
    this.scrollSource = null;
    this.scrollSourceBound = false;

    if (!this.svg || !this.path || !this.template) return;

    var self = this;

    this.onScroll = function () {
      if (self.ticking) return;
      self.ticking = true;
      window.requestAnimationFrame(function () {
        self.ticking = false;
        self.update();
      });
    };

    this.onViewportChange = function () {
      self.measure();
      self.bindScrollSource();
      self.update();
    };

    this.buildNodes();
    this.bindScrollSource();

    window.addEventListener('scroll', this.onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () {
        self.onViewportChange();
      });
      this.resizeObserver.observe(this.root);
      if (this.progressHost) this.resizeObserver.observe(this.progressHost);
    } else {
      window.addEventListener('resize', this.onViewportChange);
    }

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', this.onViewportChange);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(this.onViewportChange);
    }

    // Remeasure after layout — progress bar may be display:none on first paint
    window.requestAnimationFrame(function () {
      self.onViewportChange();
      window.requestAnimationFrame(function () {
        self.onViewportChange();
      });
    });
  }

  Separator.prototype.buildNodes = function () {
    for (var i = 0; i < 2; i += 1) {
      var node = document.createElementNS(SVG_NS, 'g');
      var visual = this.template.cloneNode(true);

      node.setAttribute('class', 'separator__node');
      visual.removeAttribute('data-separator-node-template');
      visual.setAttribute('class', 'separator__node-visual');
      node.appendChild(visual);
      this.nodesLayer.appendChild(node);
      this.nodes.push(node);
    }
  };

  Separator.prototype.bindScrollSource = function () {
    var selector = this.root.getAttribute('data-separator-scroll-source');
    if (!selector) {
      this.scrollSource = null;
      return;
    }

    var scope =
      this.root.closest('.sticky-image-richtext') ||
      this.root.closest('[data-sticky-image-progress]') ||
      document;
    var source = scope.querySelector(selector);

    if (source === this.scrollSource) {
      if (this.scrollSource && !this.scrollSourceBound) {
        this.scrollSource.addEventListener('scroll', this.onScroll, { passive: true });
        this.scrollSourceBound = true;
      }
      return;
    }

    if (this.scrollSource && this.scrollSourceBound) {
      this.scrollSource.removeEventListener('scroll', this.onScroll);
      this.scrollSourceBound = false;
    }

    this.scrollSource = source;

    if (this.scrollSource) {
      this.scrollSource.addEventListener('scroll', this.onScroll, { passive: true });
      this.scrollSourceBound = true;

      if (this.resizeObserver) {
        this.resizeObserver.observe(this.scrollSource);
      }
    }
  };

  Separator.prototype.measure = function () {
    if (!this.nodes.length) return;

    var width = Math.ceil(this.svg.getBoundingClientRect().width);

    // Skip invalid layout (e.g. display:none) — keep previous geometry
    if (width < INSET * 2 + 2) return;

    this.startX = INSET;
    this.endX = Math.max(width - INSET, this.startX + 1);

    // Keep a 1:1 user-unit mapping so node circles stay round
    this.svg.setAttribute('viewBox', '0 0 ' + width + ' 40');
    this.svg.setAttribute('width', String(width));
    this.svg.setAttribute('height', '40');
    this.svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    this.path.setAttribute('d', 'M ' + this.startX + ' ' + TRACK_Y + ' H ' + this.endX);
    this.lineGradient.setAttribute('x1', String(this.startX));
    this.lineGradient.setAttribute('x2', String(this.endX));
    this.lineGradient.setAttribute('y1', String(TRACK_Y));
    this.lineGradient.setAttribute('y2', String(TRACK_Y));

    this.nodes[0].setAttribute('transform', 'translate(' + this.startX + ' ' + TRACK_Y + ')');
    this.nodes[1].setAttribute('transform', 'translate(' + this.endX + ' ' + TRACK_Y + ')');
  };

  Separator.prototype.setProgress = function (progress) {
    progress = Math.min(Math.max(progress, 0), 1);

    if (reducedMotion.matches) {
      this.path.style.strokeDashoffset = '0';
      this.applyStates(1, false);
      this.litIndex = 1;
      this.syncProgressHost(1);
      return;
    }

    this.path.style.strokeDashoffset = String(1 - progress);
    this.syncProgressHost(progress);

    var lit = progress >= 0.92 ? 1 : progress > 0.04 ? 0 : -1;

    if (lit < 0) {
      this.applyStates(-1, false);
      this.litIndex = -1;
      return;
    }

    if (lit !== this.litIndex) {
      this.applyStates(lit, lit > this.litIndex);
      this.litIndex = lit;
    }
  };

  Separator.prototype.syncProgressHost = function (progress) {
    if (!this.progressHost) return;
    this.progressHost.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
  };

  Separator.prototype.update = function () {
    if (this.scrollSource) {
      // Only drive from horizontal scroll on mobile; desktop hides the bar
      if (!mobileQuery.matches) {
        this.path.style.strokeDashoffset = '1';
        this.applyStates(-1, false);
        this.litIndex = -1;
        return;
      }

      var max = this.scrollSource.scrollWidth - this.scrollSource.clientWidth;
      var progress = max > 1 ? this.scrollSource.scrollLeft / max : 0;

      if (reducedMotion.matches) {
        this.path.style.strokeDashoffset = '0';
        this.applyStates(1, false);
        this.litIndex = 1;
        this.syncProgressHost(1);
        return;
      }

      progress = Math.min(Math.max(progress, 0), 1);
      this.path.style.strokeDashoffset = String(1 - progress);
      this.syncProgressHost(progress);

      // Start node lit while scrolling; end node lights near completion
      var lit = progress >= 0.92 ? 1 : 0;
      if (lit !== this.litIndex) {
        this.applyStates(lit, lit > this.litIndex);
        this.litIndex = lit;
      }
      return;
    }

    // Draw over a short viewport window as the separator crosses mid-screen
    var bridgeTop = this.root.getBoundingClientRect().top;
    var start = window.innerHeight * 0.72;
    var end = window.innerHeight * 0.38;
    var progress = (start - bridgeTop) / Math.max(start - end, 1);
    this.setProgress(progress);
  };

  Separator.prototype.applyStates = function (lit, arriving) {
    this.nodes.forEach(function (node, index) {
      node.classList.remove('is-active', 'is-passed', 'is-arriving');

      if (lit < 0) return;

      if (index < lit) {
        node.classList.add('is-passed');
      } else if (index === lit) {
        node.classList.add('is-active');

        if (arriving && !reducedMotion.matches) {
          node.classList.add('is-arriving');
          window.setTimeout(function () {
            node.classList.remove('is-arriving');
          }, PULSE_DURATION);
        }
      }
    });
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-separator]');

    Array.prototype.forEach.call(roots, function (root) {
      if (root.separatorInstance) return;
      root.separatorInstance = new Separator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
